import { Customer } from '../../../shared/types';

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

export interface ValidationOptions {
  skipContactFields?: boolean; // Kontaktfelder nicht validieren (EditCustomerModal - Felder sind im Ansprechpartner-Tab)
}

export const validateCustomerData = (data: Partial<Customer>, options?: ValidationOptions): ValidationResult => {
  const errors: Record<string, string> = {};
  const skipContact = options?.skipContactFields ?? false;

  // Pflichtfelder
  if (!data.company || data.company.trim() === '') {
    errors.company = 'Firma ist erforderlich';
  }

  // E-Mail nur validieren wenn Kontaktfelder aktiv (CreateCustomerModal)
  if (!skipContact) {
    if (!data.email || data.email.trim() === '') {
      errors.email = 'E-Mail ist erforderlich';
    } else if (!isValidEmail(data.email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }
  }

  if (!data.mandantennummer || data.mandantennummer.trim() === '') {
    errors.mandantennummer = 'Mandantennummer ist erforderlich';
  }

  // Telefonnummern-Validierung (optional, aber wenn vorhanden, dann DIN 5008 konform)
  if (data.telefon && data.telefon.trim() !== '') {
    const phoneValidation = validateAndFormatPhoneNumber(data.telefon);
    if (!phoneValidation.valid) {
      errors.telefon = phoneValidation.error || 'Ungültige Telefonnummer';
    }
  }

  // Lohnlauf-Validierung
  if (data.erster_lohnlauf_tag !== null && data.erster_lohnlauf_tag !== undefined) {
    if (data.erster_lohnlauf_tag < 1 || data.erster_lohnlauf_tag > 31) {
      errors.erster_lohnlauf_tag = 'Tag muss zwischen 1 und 31 liegen';
    }
  }

  if (data.zweiter_lohnlauf_tag !== null && data.zweiter_lohnlauf_tag !== undefined) {
    if (data.zweiter_lohnlauf_tag < 1 || data.zweiter_lohnlauf_tag > 31) {
      errors.zweiter_lohnlauf_tag = 'Tag muss zwischen 1 und 31 liegen';
    }
  }

  // Lohnschein-Versand-Validierung (Stufe 3.2, analog Lohnlauf)
  if (data.lohnschein_versand_tag !== null && data.lohnschein_versand_tag !== undefined) {
    if (data.lohnschein_versand_tag < 1 || data.lohnschein_versand_tag > 31) {
      errors.lohnschein_versand_tag = 'Tag muss zwischen 1 und 31 liegen';
    }
  }

  // Konsistenz-Prüfungen nur wenn Kontaktfelder aktiv
  if (!skipContact) {
    if (data.anrede === 'Du' && !data.vorname) {
      errors.vorname = 'Vorname ist bei "Du" Anrede erforderlich';
    }

    if (data.geschlecht && data.anrede === 'Sie' && !data.nachname) {
      errors.nachname = 'Nachname ist für personalisierte Anrede erforderlich';
    }
  }

  // Abweichender Rechnungsempfänger: Die Rechnungs-PDF zeichnet die Namenszeile
  // im Empfängerblock nur, wenn Anrede UND Nachname vorhanden sind. Ohne Anrede
  // würde die Zeile kommentarlos verschwinden, deshalb ist sie hier Pflicht,
  // sobald überhaupt ein abweichender Name gepflegt wird.
  const abweichenderVorname = (data.rechnung_empfaenger_vorname || '').trim();
  const abweichenderNachname = (data.rechnung_empfaenger_nachname || '').trim();

  if ((abweichenderVorname || abweichenderNachname) && !data.rechnung_empfaenger_anrede) {
    errors.rechnung_empfaenger_anrede = 'Anrede ist beim abweichenden Rechnungsempfänger erforderlich';
  }

  if (abweichenderVorname && !abweichenderNachname) {
    errors.rechnung_empfaenger_nachname = 'Nachname ist beim abweichenden Rechnungsempfänger erforderlich';
  }

  // Offset-Validierung
  if (data.erster_lohnlauf_monat_offset !== null && data.erster_lohnlauf_monat_offset !== undefined) {
    if (data.erster_lohnlauf_monat_offset < -12 || data.erster_lohnlauf_monat_offset > 12) {
      errors.erster_lohnlauf_monat_offset = 'Offset muss zwischen -12 und 12 liegen';
    }
  }

  if (data.zweiter_lohnlauf_monat_offset !== null && data.zweiter_lohnlauf_monat_offset !== undefined) {
    if (data.zweiter_lohnlauf_monat_offset < -12 || data.zweiter_lohnlauf_monat_offset > 12) {
      errors.zweiter_lohnlauf_monat_offset = 'Offset muss zwischen -12 und 12 liegen';
    }
  }

  if (data.lohnschein_versand_monat_offset !== null && data.lohnschein_versand_monat_offset !== undefined) {
    if (data.lohnschein_versand_monat_offset < -12 || data.lohnschein_versand_monat_offset > 12) {
      errors.lohnschein_versand_monat_offset = 'Offset muss zwischen -12 und 12 liegen';
    }
  }

  // Relative Spezialfristen: Ist ein Lohnlauf-Bezug gesetzt, muss der Betrag des
  // Werktage-Offsets zwischen 1 und 60 liegen (das Vorzeichen kodiert die Richtung).
  const spezialfristBezuege: Array<{ bezugKey: keyof Customer; offsetKey: keyof Customer; errorKey: string }> = [
    { bezugKey: 'lohnschein_versand_bezug_lohnlauf', offsetKey: 'lohnschein_versand_bezug_werktage_offset', errorKey: 'lohnschein_versand_bezug_werktage_offset' },
    { bezugKey: 'oppm_qr_versand_bezug_lohnlauf', offsetKey: 'oppm_qr_versand_bezug_werktage_offset', errorKey: 'oppm_qr_versand_bezug_werktage_offset' },
    { bezugKey: 'monatsuebersicht_qr_versand_bezug_lohnlauf', offsetKey: 'monatsuebersicht_qr_versand_bezug_werktage_offset', errorKey: 'monatsuebersicht_qr_versand_bezug_werktage_offset' }
  ];
  spezialfristBezuege.forEach(({ bezugKey, offsetKey, errorKey }) => {
    const bezug = data[bezugKey];
    if (bezug !== null && bezug !== undefined) {
      const offset = data[offsetKey] as number | null | undefined;
      const betrag = Math.abs(offset ?? 0);
      if (betrag > 60) {
        errors[errorKey] = 'Werktage-Offset darf höchstens 60 Werktage betragen';
      }
    }
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validiert und formatiert deutsche Telefonnummern nach DIN 5008
 * Akzeptierte Eingabeformate:
 * - +49 221 12345678
 * - +49 (0)221 12345678
 * - 0221 12345678
 * - 0221/12345678
 * - 0221-12345678
 * 
 * Rückgabe: Formatierte Nummer im DIN 5008 Format (+49 221 12345678) oder null bei Fehler
 */
export const validateAndFormatPhoneNumber = (phone: string): { valid: boolean; formatted?: string; error?: string } => {
  if (!phone || phone.trim() === '') {
    return { valid: true }; // Leeres Feld ist erlaubt (optional)
  }

  // Entferne alle Leerzeichen, Klammern, Schrägstriche und Bindestriche für die Validierung
  const cleaned = phone.replace(/[\s()\-/]/g, '');

  // Prüfe auf gültige Zeichen (nur Zahlen und optional führendes +)
  if (!/^\+?\d+$/.test(cleaned)) {
    return {
      valid: false,
      error: 'Telefonnummer darf nur Zahlen, Leerzeichen, +, -, /, ( und ) enthalten'
    };
  }

  // Internationales Format: +49...
  if (cleaned.startsWith('+49')) {
    const nationalNumber = cleaned.substring(3);
    
    // Mindestlänge prüfen (Vorwahl + Nummer, mindestens 6 Ziffern)
    if (nationalNumber.length < 6) {
      return {
        valid: false,
        error: 'Telefonnummer zu kurz (mindestens 6 Ziffern nach Ländercode)'
      };
    }

    // Format: +49 [Vorwahl] [Rufnummer]
    // Extrahiere Vorwahl (2-5 Ziffern) und Rufnummer
    const vorwahlMatch = nationalNumber.match(/^(\d{2,5})(\d+)$/);
    if (!vorwahlMatch) {
      return {
        valid: false,
        error: 'Ungültiges Format der Telefonnummer'
      };
    }

    const [, vorwahl, rufnummer] = vorwahlMatch;
    return {
      valid: true,
      formatted: `+49 ${vorwahl} ${rufnummer}`
    };
  }

  // Nationales Format: 0...
  if (cleaned.startsWith('0')) {
    const nationalNumber = cleaned.substring(1);
    
    // Mindestlänge prüfen
    if (nationalNumber.length < 6) {
      return {
        valid: false,
        error: 'Telefonnummer zu kurz (mindestens 6 Ziffern nach der 0)'
      };
    }

    // Extrahiere Vorwahl (2-5 Ziffern) und Rufnummer
    const vorwahlMatch = nationalNumber.match(/^(\d{2,5})(\d+)$/);
    if (!vorwahlMatch) {
      return {
        valid: false,
        error: 'Ungültiges Format der Telefonnummer'
      };
    }

    const [, vorwahl, rufnummer] = vorwahlMatch;
    return {
      valid: true,
      formatted: `+49 ${vorwahl} ${rufnummer}`
    };
  }

  return {
    valid: false,
    error: 'Telefonnummer muss mit +49 oder 0 beginnen'
  };
};
